import { useState } from 'react';
import { homeSetupAPI } from '../../../../../services/redwolfSdkAPI';
import Modal from '../../../../common/components/Modal';
import PinInput from './PinInput';
import * as Sentry from '@sentry/react';
import { useGoogleAnalytics } from '../../../../common/hooks/useGoogleAnalytics';
import useTechnicalErrorMessage from '../../../../common/hooks/useTechnicalErrorMessage';
import Spinner from '../../../../common/components/Spinner';
import { notification } from '../../../../../utils/constants';
import { openNotification } from '../../../../../store/commonSlice';
import { useDispatch } from 'react-redux';
import useRequestRedwolfService from '../../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../../utils/localStorage';
import { setAddDeviceStatus } from '../../../../../store/deviceSlice';

interface DeviceProps {
	isDeviceAdded: (isNewDeviceAdded: boolean) => void;
}

const AddDevice = ({ isDeviceAdded }: DeviceProps) => {
	const [isAddTvsInfoModalOpen, setIsAddTvsInfoModalOpen] = useState(false);
	const [devicePin, setDevicePin] = useState('');
	const [clearInput, setClearInput] = useState(false);
	const [pinError, setPinError] = useState('');
	const [isDevicePinValid, setIsDevicePinValid] = useState(false);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();

	const transaction = Sentry.startInactiveSpan({ name: 'Add Device' });

	const dispatch = useDispatch();
	const token = getToken();

	const onPinInputComplete = (pinValues: string[], isValid: boolean) => {
		const pin = pinValues.join('');
		setIsDevicePinValid(isValid);
		setDevicePin(pin);
	};

	const onInputChange = () => {
		setPinError('');
	};

	const handleModalHide = () => {
		setClearInput(true);
		setIsAddTvsInfoModalOpen(false);
	};

	const handleModalOpen = () => {
		setClearInput(false);
		setIsDevicePinValid(false);
		setPinError('');
		setIsAddTvsInfoModalOpen(true);
	};

	const devicePinSubmit = (event: any) => {
		event.preventDefault();
		setIsSpinnerLoading(true);
		requestRedwolfService(homeSetupAPI.linkNonce(token, devicePin))
			.then((sdkResponse: any) => {
				const responseText = sdkResponse.response.constructor.$metadata$.simpleName;
				if (responseText === 'RegistrationSuccess' && sdkResponse.response.deviceId) {
					setIsSpinnerLoading(false);
					setClearInput(true);
					isDeviceAdded(true);
					dispatch(
						openNotification({
							type: 'SUCCESS',
							heading: 'Success',
							body: notification.message.deviceLinkedSuccess
						})
					);
					dispatch(setAddDeviceStatus(true));
					setIsAddTvsInfoModalOpen(false);
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_LINK_ERROR', { item_title: 'Success' });
				} else {
					setIsSpinnerLoading(false);
					isDeviceAdded(false);
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_LINK_ERROR', { item_title: 'Failure' });

					if (responseText === 'DeviceNameMustBeUnique') {
						setPinError('DEVICE_NAME_MUST_BE_UNIQUE');
					} else {
						setPinError('INVALID_CODE');
					}
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				isDeviceAdded(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_LINK_ERROR', { item_title: 'Failure' });
				Sentry.captureException(err);
			});
	};

	return (
		<>
			<div className="subs-head">
				<h2 className="ac-title">TVs</h2>

				<div className="subs-btn">
					<button
						type="submit"
						className="primary-button-small link-device-btn"
						aria-label="Add TV to the list"
						onClick={handleModalOpen}>
						Add TV
					</button>
				</div>
			</div>

			<Modal
				containerId="addTvCard"
				containerClasses="add-your-tv-modal"
				show={isAddTvsInfoModalOpen}
				onHide={handleModalHide}
				heading="Add your TV">
				<>
					<form className="edit-modal-form" id="addTvAccount" method="POST">
						<div className="subheadtitle">Enter the code shown on your TV.</div>

						<div className="add-tv-sound">
							<PinInput
								isPinError={pinError}
								onPinInputComplete={onPinInputComplete}
								onInputChange={onInputChange}
								clearInput={clearInput}
							/>
						</div>
						<div className="subheadbottom">
							<p>
								To add a TV to your VIZIO Account, navigate to “Extras” from the SmartCast Home
								screen. Select “VIZIO Account” and follow the on screen instructions.
							</p>
						</div>
						<div className="form-field content-right">
							<button
								type="submit"
								className="primary-button"
								id="addTvButton"
								aria-label="Add TV"
								onClick={(event) => devicePinSubmit(event)}
								disabled={!isDevicePinValid}>
								Add TV
							</button>
						</div>
					</form>
					{isSpinnerLoading && <Spinner />}
				</>
			</Modal>
		</>
	);
};

export default AddDevice;
